import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { QueryParams } from "./QueryParams";
import { ChangeEvent, createRef, JSX } from "react";
import { ITreeViewItem, ITreeViewItemWithChildren, IRouterProps, withRouting, AlertDialog } from "@iconics/components";
import { QueryParamsHelper } from "../../../../Common/QueryParamsHelper";
import { AssetWorXPlusTreeView } from "@iconics/components";
import { LanguageValuesHelper } from "@iconics/localisation";
import { Card, ConfirmDialog, GridContainer, GridItem, LayoutContainer, LayoutContent } from "@iconics/components";
import { Guid, PagedResponse, TypeHelper } from "@iconics/core";
import { ApiConstants } from "../../../../Providers.Api/ApiConstants";
import { SerendieSymbol } from "@serendie/symbols";
import { ImportResultWrapper, INodeData } from "./DataModels";
import { RootNodeId } from "../../../../Providers.StaticData/StaticDataProvider";
import { NodesImportDialog } from "./NodesImportDialog";

export class NodesTreeView extends IbssPage<INodesTreeViewProps, INodesTreeViewState>
{
    private queryParams = new QueryParamsHelper(new QueryParams(), firstLoad => this.handleQueryParamsUpdated(firstLoad), this.props.navigate);
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: INodesTreeViewProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            nodes: [],
            importNodeId: 0,
            importNodes: [],
            showConfirmDelete: false,
            showImportDialog: false,
            refreshTreeToken: Guid.new().toString(),
        };
    }

    private get menuNodeId(): number | undefined 
    {
        return TypeHelper.toNumber(this.state.menuNode?.id) ?? undefined;
    }

    public async componentDidMount()
    {
        const labels = appContext().labels;
        this.pageTitle = labels.Platform_AssetWorX_MenuTitle;
        this.queryParams.evaluate();
    }

    public componentDidUpdate(prevProps: INodesTreeViewProps)
    {
        this.queryParams.evaluate(this.props.location, prevProps.location);
    }

    public async handleQueryParamsUpdated(firstLoad: boolean)
    {
        const { expandedNodeId } = this.queryParams.value;
        if (expandedNodeId && firstLoad)
        {
            this.setState({ selectedNodeId: expandedNodeId });
        }
    }

    private async handleGetNodes(parentId?: string): Promise<ITreeViewItem[]>
    {
        try
        {
            const filter = (parentId ? `Parent_ID eq ${parentId}` : `ID eq ${RootNodeId}`);

            const nodesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.get<PagedResponse<INodeData[]>>({
                tenancy: appContext().tenantId,
                filter: `${filter} and Deleted eq false`,
                orderBy: 'SortOrder asc',
                top: ApiConstants.largeODataTop,
            });

            const nodes = nodesData
                .value
                .map(i => ({
                    id: i.Id.toString(),
                    parentId: i.Parent_ID?.toString() ?? undefined,
                    name: this.languageValuesHelper.getValue(this.languageValuesHelper.fromString(i.DisplayName)) || i.Name,

                })) satisfies ITreeViewItem[];

            return nodes;
        }
        catch
        {
            return [];
        }
    }

    private async handleGetAncestorNodes(nodeId: string): Promise<string[]>
    {
        try
        {
            let ancestorIds = [nodeId];
            do
            {
                const ancestorData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.get<PagedResponse<INodeData[]>>({
                    tenancy: appContext().tenantId,
                    filter: `ID eq ${ancestorIds[0]} and Deleted eq false`,
                    top: 1,
                });

                const ancestor = ancestorData.value[0];
                if (!ancestor.Parent_ID)
                {
                    break;
                }
                ancestorIds = [ancestor.Parent_ID.toString(), ...ancestorIds];
            }
            while (true);

            return ancestorIds;
        }
        catch
        {
            return [];
        }
    }

    private handleAdd(node: ITreeViewItem): void
    {
        this.props.navigate(`/admin/nodes/${node.id}/children/new`);
    }

    private handleEdit(node: ITreeViewItem): void
    {
        this.props.navigate(`/admin/nodes/${node.id}/edit?source=tree`);
    }

    private handleTryDelete(node: ITreeViewItem): void
    {
        this.setState({ showConfirmDelete: true, menuNode: node });
    }

    private handleSwitchToGraphView(node: ITreeViewItem): void
    {
        this.props.navigate(`/admin/nodes/graph-view/${node.id}`);
    }

    private async handleImport(node: ITreeViewItem): Promise<void>
    {
        const nodeId = TypeHelper.toNumber(node.id);
        if (!nodeId)
        {
            return;
        }

        const label = this.buildNodePath(this.state.nodes, node.id);

        this.setState({
            showImportDialog: true,
            importNodes: [{ label: label, id: nodeId }],
            importNodeId: nodeId
        });
    }

    private buildNodePath(nodes: ITreeViewItemWithChildren[], nodeId: string): string
    {
        const found = nodes.find(x => x.id === nodeId);

        if (found)
        {
            return found.name;
        }

        for (const node of nodes)
        {
            if (!node.children)
                continue;
            
            const foundInChildren = this.buildNodePath(node.children, nodeId);
            if (foundInChildren)
                return `${node.name} > ${foundInChildren}`;
        }
        return '';
    }

    private handleImportDialogClose(refresh: boolean): void
    {
        if (refresh)
            this.setState({
                showImportDialog: false,
                refreshTreeToken: Guid.new().toString(),
            });
        else
            this.setState({
                showImportDialog: false,
            });
    }

    private async onDeleteConfirm(): Promise<void> 
    {
        await this.loading(async () => 
        {
            if (this.menuNodeId && this.state.menuNode)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.delete({
                    nodeId: this.menuNodeId,
                    tenancy: appContext().tenantId,
                });

                const updatedNodes = this.removeNodeById(this.state.nodes, this.state.menuNode.id);
                this.setState({ nodes: updatedNodes });
            }
        });
        this.setState({ showConfirmDelete: false });
    }

    private removeNodeById(nodes: ITreeViewItemWithChildren[], idToRemove: string): ITreeViewItemWithChildren[]
    {
        return nodes
            .filter(i => i.id !== idToRemove)
            .map(node =>
            {
                return {
                    ...node,
                    children: (node.children && this.removeNodeById(node.children, idToRemove)),
                };
            });
    }

    public render(): JSX.Element
    {
        const labels = appContext().labels

        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            icon={<SerendieSymbol name='layer' />}
                            title={labels.Platform_AssetWorX_NodeHierarchyTitle}
                            subTitle={labels.Platform_AssetWorX_NodeHierarchySubTitle}
                        >
                            <GridContainer>
                                <GridItem
                                    style={{ padding: 'var(--spacing-sd-system-dimension-spacing-extra-large)' }}
                                >
                                    <AssetWorXPlusTreeView
                                        nodes={this.state.nodes}
                                        onNodesChange={i => this.setStateAsync({ nodes: i })}
                                        menu={[
                                            { id: 'asset-menu-0', name: labels.Platform_AssetWorX_Add, onClick: i => this.handleAdd(i) },
                                            { id: 'asset-menu-1', name: labels.Platform_AssetWorX_Edit, onClick: i => this.handleEdit(i) },
                                            { id: 'asset-menu-2', name: labels.Platform_AssetWorX_Import, onClick: i => this.handleImport(i) },
                                            { id: 'asset-menu-3', name: labels.Platform_AssetWorX_SwitchToGraphView, onClick: i => this.handleSwitchToGraphView(i) },
                                            { id: 'asset-menu-4', name: labels.Platform_AssetWorX_Delete, onClick: i => this.handleTryDelete(i) },
                                        ]}
                                        selectedNodeId={this.state.selectedNodeId}
                                        expandedNodeIds={[RootNodeId.toString()]}
                                        onGetNodes={parentId => this.handleGetNodes(parentId)}
                                        onGetAncestorNodes={i => this.handleGetAncestorNodes(i)}
                                        refreshToken={this.state.refreshTreeToken}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                </LayoutContainer>

                <ConfirmDialog
                    show={this.state.showConfirmDelete}
                    header={labels.Platform_ConfirmDelete_Title}
                    content={labels.Platform_Node_ConfirmDeleteMsg}
                    okButtonLabel={labels.Platform_CommonAction_Delete}
                    confirmButtonColor='error'
                    confirmButtonIcon={<SerendieSymbol name='trash' />}
                    onConfirm={() => this.onDeleteConfirm()}
                    onClose={() => this.setState({ showConfirmDelete: false })}
                />
                <NodesImportDialog
                    open={this.state.showImportDialog}
                    nodeId={this.state.importNodeId}
                    nodes={this.state.importNodes}
                    onClose={(success) => this.handleImportDialogClose(success)}
                />
            </>
        );
    }
}


export default withRouting(NodesTreeView);

export interface INodesTreeViewProps extends IRouterProps { }

export interface INodesTreeViewState
{
    importNodeId: number;
    importNodes: {label: string, id: number}[]
    nodes: ITreeViewItemWithChildren[];
    menuNode?: ITreeViewItem;
    showConfirmDelete: boolean;
    showImportDialog: boolean;
    selectedNodeId?: string;
    refreshTreeToken: string;
}
